import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, Stack, Box, Typography,
  FormControl, InputLabel, Select, MenuItem, IconButton, Checkbox, Divider, Chip, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { useTranslation } from 'react-i18next';
import { useCreateMeeting, useUpdateMeeting } from '../../../api/hooks/useMeetings';
import { useOffenePunkte, useCreateOffenerPunkt } from '../../../api/hooks/useOffenePunkte';
import { useNotifications } from '../../../context/NotificationContext';
import { AttachmentList } from '../../../components/common/AttachmentList';
import { PmRichTextEditor } from '../../../components/pm';
import { formatApiError } from '../../../api/client';
import { combineDateTimeToIso, splitIsoDateTime } from '../lib/dateTimeIso';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

/** Finale 7 Meetingtypen (pm-api CHECK, migr 056). */
export const MEETING_TYPES = [
  'Kick-Off', 'Lenkungskreis', 'Abnahme / Review', 'Team Meeting',
  'Retrospektive', 'Phasenabschluss / Projektabschluss', 'Sonstige',
] as const;

export interface Teilnehmer { name: string; eingeladen: boolean; anwesend: boolean; }

export interface MeetingFull {
  id: string;
  titel: string | null;
  typ: string | null;
  datum: string | null;
  ort: string | null;
  teilnehmer: string | null;
  teilnehmer_liste: Teilnehmer[] | null;
  started_at: string | null;
  ended_at: string | null;
  notes: string | null;
}

const toIso = (d: Dayjs | null): string | null =>
  d ? combineDateTimeToIso(d.format('YYYY-MM-DD'), d.format('HH:mm')) : null;
const fromIso = (iso: string | null | undefined): Dayjs | null => {
  if (!iso) return null;
  const { date, time } = splitIsoDateTime(iso);
  return date ? dayjs(`${date}T${time || '00:00'}`) : null;
};

interface FormState {
  titel: string;
  typ: string;
  datum: Dayjs | null;
  ort: string;
  teilnehmer_liste: Teilnehmer[];
  started_at: Dayjs | null;
  ended_at: Dayjs | null;
  notes: string;
}

const EMPTY: FormState = {
  titel: '', typ: '', datum: dayjs(), ort: '', teilnehmer_liste: [], started_at: null, ended_at: null, notes: '',
};

/**
 * Grosse Meeting-Maske: alle Planungsfelder + Teilnehmer-Management
 * (eingeladen/anwesend abhaken, spontane ergaenzbar) + Start/Ende-Lifecycle
 * (Klick = jetzt, beide editierbar) + offene Punkte aus dem Meeting + Anhaenge.
 */
export function MeetingDialog({
  open, projectId, meeting, onClose,
}: {
  open: boolean;
  projectId: string;
  meeting: MeetingFull | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();

  const createMut = useCreateMeeting(projectId);
  const updateMut = useUpdateMeeting(projectId);

  // Nach Erst-Anlage gesetzt -> schaltet OP-/Anhang-Sektionen frei.
  const [currentId, setCurrentId] = useState<string | null>(meeting?.id ?? null);
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setCurrentId(meeting?.id ?? null);
    if (meeting) {
      setForm({
        titel: meeting.titel ?? '', typ: meeting.typ ?? '',
        datum: fromIso(meeting.datum) ?? dayjs(), ort: meeting.ort ?? '',
        teilnehmer_liste: Array.isArray(meeting.teilnehmer_liste) ? meeting.teilnehmer_liste : [],
        started_at: fromIso(meeting.started_at), ended_at: fromIso(meeting.ended_at),
        notes: meeting.notes ?? '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, meeting]);

  const payload = (f: FormState) => ({
    titel: f.titel.trim() || null,
    typ: f.typ || null,
    datum: toIso(f.datum),
    ort: f.ort.trim() || null,
    teilnehmer_liste: f.teilnehmer_liste,
    started_at: toIso(f.started_at),
    ended_at: toIso(f.ended_at),
    notes: f.notes.trim() || null,
  });

  const persist = async (f: FormState): Promise<string | null> => {
    try {
      if (currentId) {
        await updateMut.mutateAsync({ id: currentId, data: payload(f) });
        return currentId;
      }
      const resp = await createMut.mutateAsync(payload(f));
      const id = (resp as unknown as { data?: { id?: string } } | undefined)?.data?.id ?? null;
      if (id) setCurrentId(id);
      return id;
    } catch (err) {
      notify(formatApiError(err), 'error');
      return null;
    }
  };

  const handleSave = async () => {
    const id = await persist(form);
    if (id) { notify(tc('status.saved', 'Gespeichert'), 'success'); onClose(); }
  };

  const handleStart = async () => {
    const next = { ...form, started_at: form.started_at ?? dayjs() };
    setForm(next);
    const id = await persist(next);
    if (id) notify(t('meetings.started', 'Meeting gestartet'), 'success');
  };

  const handleEnd = async () => {
    const next = { ...form, ended_at: dayjs() };
    setForm(next);
    const id = await persist(next);
    if (id) notify(t('meetings.ended', 'Meeting beendet'), 'success');
  };

  // Teilnehmer-Operationen
  const addTeilnehmer = (name: string, spontan: boolean) => {
    const n = name.trim();
    if (!n) return;
    setForm((f) => ({
      ...f,
      teilnehmer_liste: [...f.teilnehmer_liste, { name: n, eingeladen: !spontan, anwesend: spontan }],
    }));
  };
  const patchTeilnehmer = (idx: number, patch: Partial<Teilnehmer>) =>
    setForm((f) => ({ ...f, teilnehmer_liste: f.teilnehmer_liste.map((tn, i) => (i === idx ? { ...tn, ...patch } : tn)) }));
  const removeTeilnehmer = (idx: number) =>
    setForm((f) => ({ ...f, teilnehmer_liste: f.teilnehmer_liste.filter((_, i) => i !== idx) }));

  const counts = useMemo(() => {
    const eingeladen = form.teilnehmer_liste.filter((tn) => tn.eingeladen).length;
    const anwesend = form.teilnehmer_liste.filter((tn) => tn.anwesend).length;
    const abwesend = form.teilnehmer_liste.filter((tn) => tn.eingeladen && !tn.anwesend).length;
    return { eingeladen, anwesend, abwesend };
  }, [form.teilnehmer_liste]);

  const busy = createMut.isPending || updateMut.isPending;
  const started = !!form.started_at;
  const ended = !!form.ended_at;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <AppDialogTitle>
          {meeting ? t('meetings.editTitle', 'Meeting bearbeiten') : t('meetings.createTitle', 'Neues Meeting')}
        </AppDialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Planung */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={t('meetings.titel', 'Name')} value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} fullWidth autoFocus />
              <FormControl fullWidth>
                <InputLabel>{t('meetings.typ', 'Typ')}</InputLabel>
                <Select value={form.typ} label={t('meetings.typ', 'Typ')} onChange={(e) => setForm({ ...form, typ: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  {MEETING_TYPES.map((tp) => <MenuItem key={tp} value={tp}>{tp}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker label={t('meetings.zeitpunkt', 'Zeitpunkt')} value={form.datum} onChange={(v) => setForm({ ...form, datum: v })} ampm={false} slotProps={{ textField: { fullWidth: true } }} />
              <TextField label={t('meetings.ort', 'Ort')} value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} fullWidth />
            </Stack>

            <Divider />

            {/* Teilnehmer */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-base)' }}>{t('meetings.teilnehmer', 'Teilnehmer')}</Typography>
                <Chip size="small" label={`${t('meetings.eingeladen', 'Eingeladen')}: ${counts.eingeladen}`} />
                <Chip size="small" color="success" label={`${t('meetings.anwesend', 'Anwesend')}: ${counts.anwesend}`} />
                <Chip size="small" label={`${t('meetings.abwesend', 'Abwesend')}: ${counts.abwesend}`} />
              </Stack>
              <TeilnehmerEditor
                items={form.teilnehmer_liste}
                onAdd={addTeilnehmer}
                onPatch={patchTeilnehmer}
                onRemove={removeTeilnehmer}
              />
            </Box>

            <Divider />

            {/* Lifecycle: gestartet / beendet */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-base)', mb: 1 }}>{t('meetings.lifecycle', 'Durchführung')}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <DateTimePicker label={t('meetings.startedAt', 'Gestartet')} value={form.started_at} onChange={(v) => setForm({ ...form, started_at: v })} ampm={false} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
                <DateTimePicker label={t('meetings.endedAt', 'Beendet')} value={form.ended_at} onChange={(v) => setForm({ ...form, ended_at: v })} ampm={false} slotProps={{ textField: { fullWidth: true, size: 'small' } }} />
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                <Button variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={() => void handleStart()} disabled={busy || started}>
                  {t('meetings.startBtn', 'Meeting starten')}
                </Button>
                <Button variant="outlined" color="error" startIcon={<StopIcon />} onClick={() => void handleEnd()} disabled={busy || !started || ended}>
                  {t('meetings.endBtn', 'Meeting beenden')}
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* Protokoll */}
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', mb: 0.5 }}>
                {t('meetings.notes', 'Protokoll / Notizen')}
              </Typography>
              <PmRichTextEditor
                value={form.notes}
                onChange={(html) => setForm({ ...form, notes: html })}
                placeholder={t('meetings.notesPlaceholder', 'Protokoll — Formatierung, Listen, Emojis …')}
              />
            </Box>

            <Divider />

            {/* Offene Punkte aus dem Meeting */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-base)', mb: 1 }}>{t('meetings.opSection', 'Offene Punkte aus diesem Meeting')}</Typography>
              {currentId ? (
                <MeetingOffenePunkte projectId={projectId} meetingId={currentId} />
              ) : (
                <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                  {t('meetings.opSaveFirst', 'Meeting zuerst anlegen (Speichern/Starten), dann lassen sich offene Punkte erfassen.')}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Anhaenge */}
            <AttachmentList projectId={projectId} entityType="meeting" entityId={currentId ?? undefined} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : tc('actions.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

function TeilnehmerEditor({
  items, onAdd, onPatch, onRemove,
}: {
  items: Teilnehmer[];
  onAdd: (name: string, spontan: boolean) => void;
  onPatch: (idx: number, patch: Partial<Teilnehmer>) => void;
  onRemove: (idx: number) => void;
}) {
  const { t } = useTranslation('projects');
  const [name, setName] = useState('');
  const submit = (spontan: boolean) => { onAdd(name, spontan); setName(''); };

  return (
    <Stack spacing={0.5}>
      {items.map((tn, idx) => (
        <Stack key={idx} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: 'var(--pm-bg-subtle)' }}>
          <Typography variant="body2" sx={{ flex: 1, color: 'var(--pm-fg-base)' }}>{tn.name}</Typography>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Checkbox size="small" checked={tn.eingeladen} onChange={(e) => onPatch(idx, { eingeladen: e.target.checked })} />
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', width: 64 }}>{t('meetings.eingeladen', 'Eingeladen')}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Checkbox size="small" color="success" checked={tn.anwesend} onChange={(e) => onPatch(idx, { anwesend: e.target.checked })} />
            <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', width: 60 }}>{t('meetings.anwesend', 'Anwesend')}</Typography>
          </Stack>
          <IconButton size="small" color="error" onClick={() => onRemove(idx)} aria-label={t('meetings.removeTn', 'Teilnehmer entfernen')}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          variant="standard" size="small" fullWidth
          placeholder={t('meetings.tnPlaceholder', 'Name eingeben…')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(false); } }}
        />
        <Button size="small" startIcon={<AddIcon />} onClick={() => submit(false)} disabled={!name.trim()}>
          {t('meetings.addEingeladen', 'Eingeladen')}
        </Button>
        <Button size="small" color="success" startIcon={<AddIcon />} onClick={() => submit(true)} disabled={!name.trim()}>
          {t('meetings.addSpontan', 'Spontan anwesend')}
        </Button>
      </Stack>
    </Stack>
  );
}

interface OpLite { id: string; titel: string | null; kategorie: string | null; status: string; meeting_id?: string | null; }

function MeetingOffenePunkte({ projectId, meetingId }: { projectId: string; meetingId: string }) {
  const { t } = useTranslation('projects');
  const { notify } = useNotifications();
  const { data } = useOffenePunkte(projectId);
  const createOp = useCreateOffenerPunkt(projectId);
  const all = ((data as unknown as { data?: OpLite[] } | undefined)?.data ?? []);
  const mine = all.filter((op) => op.meeting_id === meetingId);

  const [titel, setTitel] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const n = titel.trim();
    if (!n || busy) return;
    setBusy(true);
    try {
      await createOp.mutateAsync({
        titel: n, kategorie: null, faellig: null, status: 'Offen',
        beschreibung: null, verantwortlich: null,
        meeting_id: meetingId, quelle: 'meeting',
      });
      setTitel('');
    } catch (err) { notify(formatApiError(err), 'error'); }
    finally { setBusy(false); }
  };

  return (
    <Stack spacing={0.5}>
      {mine.map((op) => (
        <Stack key={op.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'var(--pm-bg-subtle)' }}>
          <Typography variant="body2" sx={{ flex: 1, color: 'var(--pm-fg-base)' }}>{op.titel ?? '—'}</Typography>
          <Chip size="small" label={op.status} />
        </Stack>
      ))}
      {mine.length === 0 && (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>{t('meetings.opEmpty', 'Noch keine offenen Punkte aus diesem Meeting.')}</Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          variant="standard" size="small" fullWidth
          placeholder={t('meetings.opPlaceholder', 'Offenen Punkt eintragen…')}
          value={titel}
          onChange={(e) => setTitel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
        />
        <Button size="small" startIcon={<AddIcon />} onClick={() => void add()} disabled={!titel.trim() || busy}>
          {t('meetings.opAdd', 'Punkt anlegen')}
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
        {t('meetings.opDetailHint', 'Details (Rich-Text, Anhänge) im Reiter „Offene Punkte".')}
      </Typography>
    </Stack>
  );
}
